import { FormControl, FormControlProps, FormHelperText } from '@mui/material';
import { unstable_setRef as setRef } from '@mui/utils';
import type { V2FieldSize } from '@onevision-resources/design-theme';
import { type ReactNode, type Ref, useEffect, useId, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { isNonNullish } from 'remeda';
import { CommonFormFieldProps } from 'src/components/Forms/DynamicForm/dynamic-form';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import { AutoCompleteOption } from 'src/components/Forms/types';
import { useWatchConditions } from 'src/components/Forms/use-validations';
import { SelectMenu, type SelectMenuOption } from 'src/components/SelectMenu';
import { toSxArray } from 'src/utils/sx';

export type DefaultOption = AutoCompleteOption | string;
export type AutocompleteValue<T extends DefaultOption = DefaultOption> = T;

/** What an option shows besides its label, in SelectMenu's terms. */
export type FormAutocompleteOptionDetails = Pick<
  SelectMenuOption,
  'description' | 'hint' | 'icon' | 'endIcon' | 'group' | 'keywords' | 'disabled'
>;

/** A picked option, a list of them, or the typed text of a custom value. */
export type FormAutocompleteFieldValue<T extends DefaultOption = DefaultOption> =
  | T
  | T[]
  | string
  | null;

type FormAutocompleteFieldProps<T extends DefaultOption = DefaultOption> = Pick<
  FormControlProps,
  'sx' | 'fullWidth' | 'className'
> & {
  options: T[];
  label?: string;
  required?: boolean | string;
  multiple?: boolean;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  /** Replaces the label of the picked value on the trigger. */
  triggerLabel?: string;
  /** Leading icon on the trigger. */
  icon?: ReactNode;
  size?: V2FieldSize;
  /** Drops the field box, for dense property rows. */
  inline?: boolean;
  /** Draws option icons without their tinted tile, e.g. avatars. */
  plainOptionIcons?: boolean;
  /** CSS width for the menu; defaults to the trigger's width. */
  menuWidth?: string;
  clearable?: boolean;
  showErrorText?: boolean;
  emptyText?: string;
  searchPlaceholder?: string;
  getOptionLabel?: (option: T) => string;
  getOptionDetails?: (option: T) => FormAutocompleteOptionDetails;
  /** Server-side search: the options already match the query, so the menu does not filter them. */
  onSearchChange?: (query: string) => void;
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
  /** Pins an "add new" row under the options; `onAddNewItem` runs after the menu closes. */
  creatable?: boolean;
  onAddNewItem?: () => void;
  onAddNewItemLabel?: string;
  /** While a query is typed, a `Use "…"` row writes the query itself as the value. */
  allowCustomValue?: boolean;
  /** Runs after the form value is set, for side effects on other fields. */
  onChange?: (value: FormAutocompleteFieldValue<T>) => void;
  triggerRef?: Ref<HTMLButtonElement>;
} & CommonFormFieldProps<'autocomplete', AutocompleteValue<T> | null | undefined>;

export type FormAutocompleteProps<T extends DefaultOption = DefaultOption> =
  FormAutocompleteFieldProps<T> & {
    /** Initial value DynamicForm writes into the form on mount; the field itself reads the form. */
    value?: AutocompleteValue<T> | null;
  };

// The menu keys rows by non-empty strings, so an "Empty" option's blank id gets a stand-in.
const EMPTY_VALUE_KEY = '__empty__';

const toKey = (value: DefaultOption | null | undefined): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const raw = typeof value === 'string' ? value : String(value.id);
  return raw === '' ? EMPTY_VALUE_KEY : raw;
};

const NO_DEPENDENCIES: string[] = [];

const NO_CONDITIONS: NonNullable<FormAutocompleteProps['showsIf']> = [];

const defaultGetOptionLabel = (option: DefaultOption) =>
  typeof option === 'string' ? option : option.label;

/**
 * Form field for picking options that need more than a static list: `multiple`, server-side
 * search (`onSearchChange`, `hasMore`), a `creatable` row, `group` / icon / description rows via
 * `getOptionDetails`, or free text (`allowCustomValue`). The form stores the option object
 * (`{ id, label }` or a string), not a bare value. A single pick from a short static list whose
 * form value is a plain value is `FormSelect`.
 */
export const FormAutocomplete = <T extends DefaultOption = DefaultOption>({
  name,
  label,
  options,
  required,
  multiple = false,
  disabled = false,
  loading = false,
  placeholder = '',
  triggerLabel,
  icon,
  size,
  inline = false,
  plainOptionIcons = false,
  menuWidth = 'trigger',
  clearable = true,
  showErrorText = false,
  emptyText,
  searchPlaceholder,
  getOptionLabel = defaultGetOptionLabel,
  getOptionDetails,
  onSearchChange,
  hasMore,
  onLoadMore,
  loadingMore,
  creatable = false,
  onAddNewItem,
  onAddNewItemLabel,
  allowCustomValue = false,
  onChange,
  triggerRef,
  dependsOn = NO_DEPENDENCIES,
  showsIf = NO_CONDITIONS,
  sx,
  fullWidth,
  className,
  hidden,
  'data-testid': dataTestId,
}: FormAutocompleteFieldProps<T>) => {
  const triggerId = useId();
  const errorTextId = useId();
  const { control, formState, getFieldState, resetField } = useFormContext();
  const errorMsg = getFieldState(name, formState).error?.message;
  const hasErrorMsg = typeof errorMsg === 'string';
  const showsErrorText = showErrorText && hasErrorMsg;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ownsQuery = isNonNullish(onSearchChange) || allowCustomValue;

  // If the field depends on another field, reset value when the field it depends on changes.
  const dependencyValues = useWatch({ control, name: dependsOn });
  useEffect(() => {
    if (dependencyValues?.length > 0) {
      resetField(name);
    }
  }, [dependencyValues, resetField, name]);

  const shouldShow = useWatchConditions({ control, showsIf });

  const menuOptions = useMemo<SelectMenuOption[]>(
    () =>
      options.map((option) => ({
        value: toKey(option) ?? EMPTY_VALUE_KEY,
        title: getOptionLabel(option),
        testId: typeof option === 'string' ? undefined : option.testId,
        ...getOptionDetails?.(option),
      })),
    [options, getOptionLabel, getOptionDetails],
  );
  const sectioned = menuOptions.some((option) => isNonNullish(option.group));

  if (!shouldShow) {
    return null;
  }

  const findOption = (key: string | null) => options.find((option) => toKey(option) === key);

  const labelOf = (value: DefaultOption) => {
    const option = findOption(toKey(value));
    if (option) {
      return getOptionLabel(option);
    }
    return typeof value === 'string' ? value : value.label;
  };

  const changeQuery = (next: string) => {
    setQuery(next);
    onSearchChange?.(next);
  };

  return (
    <FormControl
      sx={[{ width: '100%' }, ...toSxArray(sx)]}
      fullWidth={fullWidth}
      className={className}
      hidden={hidden}
      data-testid={dataTestId}
      disabled={disabled}
      error={hasErrorMsg}
    >
      {label ? <FormFieldLabel htmlFor={triggerId}>{label}</FormFieldLabel> : null}
      <Controller
        name={name}
        control={control}
        rules={{ required }}
        render={({ field }) => {
          const fieldValue = field.value as FormAutocompleteFieldValue<T> | undefined;
          const pickedList = Array.isArray(fieldValue) ? fieldValue : [];
          const pickedSingle = Array.isArray(fieldValue) ? null : (fieldValue ?? null);

          const commit = (next: FormAutocompleteFieldValue<T>) => {
            field.onChange(next);
            onChange?.(next);
          };

          const changeOpen = (next: boolean) => {
            setOpen(next);
            if (next) {
              return;
            }
            field.onBlur();
            if (query) {
              changeQuery('');
            }
          };

          const trimmedQuery = query.trim();
          let createAction;
          if (allowCustomValue && trimmedQuery) {
            createAction = {
              label: `Use “${trimmedQuery}”`,
              onSelect: () => {
                commit(trimmedQuery);
                changeOpen(false);
              },
            };
          } else if (creatable && onAddNewItem) {
            createAction = {
              label: onAddNewItemLabel ?? 'Create new',
              onSelect: () => {
                changeOpen(false);
                onAddNewItem();
              },
            };
          }

          const pickedLabel = multiple
            ? pickedList.map(labelOf).join(', ')
            : pickedSingle && labelOf(pickedSingle);

          return (
            <SelectMenu
              ref={(node) => {
                field.ref(node);
                setRef(triggerRef, node);
              }}
              id={triggerId}
              label={label ?? name}
              variant={inline ? 'inline' : 'field'}
              size={size}
              menuWidth={menuWidth}
              options={menuOptions}
              sectioned={sectioned}
              plainOptionIcons={plainOptionIcons}
              multiple={multiple}
              value={multiple ? undefined : toKey(pickedSingle)}
              values={multiple ? pickedList.map(toKey).filter(isNonNullish) : undefined}
              triggerLabel={triggerLabel ?? (pickedLabel || undefined)}
              triggerIcon={icon}
              placeholder={placeholder}
              disabled={disabled}
              error={hasErrorMsg}
              aria-describedby={showsErrorText ? errorTextId : undefined}
              clearable={clearable}
              onClear={() => commit(multiple ? [] : null)}
              searchPlaceholder={searchPlaceholder}
              searchValue={ownsQuery ? query : undefined}
              onSearchChange={ownsQuery ? changeQuery : undefined}
              loading={loading}
              hasMore={hasMore}
              onLoadMore={onLoadMore}
              loadingMore={loadingMore}
              emptyText={emptyText}
              createAction={createAction}
              open={open}
              onOpenChange={changeOpen}
              onChange={(key) => {
                const option = findOption(key);
                if (option) {
                  commit(option);
                }
              }}
              onValuesChange={(_keys, menuOption) => {
                const isPicked = pickedList.some((value) => toKey(value) === menuOption.value);
                if (isPicked) {
                  commit(pickedList.filter((value) => toKey(value) !== menuOption.value));
                  return;
                }
                const option = findOption(menuOption.value);
                if (option) {
                  commit([...pickedList, option]);
                }
              }}
            />
          );
        }}
      />
      {showsErrorText ? <FormHelperText id={errorTextId}>{errorMsg}</FormHelperText> : null}
    </FormControl>
  );
};
